import { memo, type ReactNode } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

const workspaceAgentBubble = tailwindVariants({
  defaultVariants: {
    isPending: false,
  },
  variants: {
    variant: {
      // The 85% cap is arbitrary (not a design-system size): it leaves a gutter on the left that
      // shows user turns came from the right. The squared corner is the tail on that side.
      user: 'max-w-[85%] self-end rounded-12 rounded-tr-2 ads:bg-subtle bg-gray-3 px-3 py-2',
      userHero: 'w-full max-w-full self-end rounded-12 rounded-tr-2 ads:bg-subtle bg-gray-3 px-3 py-2',
      agent: 'w-full max-w-full self-start',
    },
    isPending: {
      // Any dimmer and the text drops below the contrast floor against the panel.
      true: 'opacity-80',
    },
  },
});

export type WorkspaceAgentBubbleVariant = NonNullable<VariantProps<typeof workspaceAgentBubble>['variant']>;

// `variant` is omitted and redeclared because `VariantProps` types it as optional, and there is no
// unstyled bubble — the variants carry every bit of the styling.
export type WorkspaceAgentBubbleProps = Omit<VariantProps<typeof workspaceAgentBubble>, 'variant'> & {
  children: ReactNode;
  'data-testid'?: string;
  variant: WorkspaceAgentBubbleVariant;
};

/**
 * Every conversation turn in the panel, so alignment, max-width, and user-side chrome live in one
 * place. User turns get the gray inset bubble capped at 85% width; assistant turns are full-width
 * plain prose (no inset box). Deliberately takes no `className`: a third shape should be a third
 * variant here rather
 * than an override at a call site.
 *
 * Always a `div`, never a `p` — an agent turn wraps whatever the markdown renderer or the block
 * renderer produces, which a paragraph may not legally contain.
 */
export const WorkspaceAgentBubble = memo(
  ({ children, 'data-testid': testId, isPending, variant }: WorkspaceAgentBubbleProps) => (
    <div
      // Dimming a pending bubble is a visual-only signal without this.
      aria-busy={isPending || undefined}
      className={workspaceAgentBubble({ isPending, variant })}
      data-testid={testId}
    >
      {children}
    </div>
  ),
);

WorkspaceAgentBubble.displayName = 'WorkspaceAgentBubble';
